/**
 * data/species.ts — the ten playable sharks of SPEC §7: fixed stats and a fixed silhouette,
 * with everything else (palette, pattern grain, proportions) re-rolled per seed.
 */

import { Rng, hashInts, hashString } from '../core/Rng.ts';
import { VOXEL } from './tuning.ts';
import { normalizeGenome } from '../voxel/Genome.ts';
import type { EyeStyle, FinParams, Pattern, SharkGenome, Snout } from '../voxel/Genome.ts';

export interface SpeciesDef {
  id: string;
  name: string;
  hull: number;
  speed: number;
  passiveName: string;
  passive: string;
  branch: string;
  unlock: 'start' | 'denticles' | 'final';
  /** Build the genome. Same (species, seed) must give a byte-identical mesh. */
  genome(seed: number): SharkGenome;
}

// ---------------------------------------------------------------------------
// Shape description
// ---------------------------------------------------------------------------
// Anatomy is stored as *ratios*, not voxels. Length and girth get +/-10% per seed;
// expressing every fin, jaw and lobe against them means the proportions survive that
// jitter untouched — which is exactly the SPEC §7 promise that "silhouette and stats are
// stable, pattern and proportions vary".

interface FinShape {
  /** span out from the body, x girth */
  height: number;
  /** along the spine, x length */
  chord: number;
  /** 0..1 backward sweep */
  rake: number;
  /** 0..1 along the spine, 0 = nose */
  position: number;
}

interface SpeciesShape {
  length: number;
  girth: number;
  taper: readonly [number, number, number, number];
  snout: Snout;
  /** x length */
  snoutLength: number;
  dorsal: FinShape;
  pectoral: FinShape;
  /** lobes x length; rake absolute. Never jittered — the caudal is half the read from above. */
  caudal: { upper: number; lower: number; rake: number };
  /** P(a sixth gill slit); a seventh is a third as likely again */
  gillBias: number;
  eyeStyle: EyeStyle;
  pattern: Pattern;
  /** seeded range; LOWER = coarser blobs = fewer triangles */
  patternScale: readonly [number, number];
  /** seeded range, voxels of nose droop */
  headDrop: readonly [number, number];
  /** base sRGB hex dorsal / flank / ventral, hue-jittered per seed */
  palette: readonly [number, number, number];
  /** x girth */
  jawWidth: number;
  toothRows: number;
  heightRatio: number;
  girthPeak: number;
  peduncle: number;
  /** x girth, 0 disables */
  secondDorsal: number;
  /** x girth, 0 disables */
  pelvicFin: number;
  /** cephalofoil half-span = girth * 2.4 * foil (CONTRACTS §16) */
  foil: number;
  scale: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const r2 = (v: number): number => Math.round(v * 100) / 100;
const r3 = (v: number): number => Math.round(v * 1000) / 1000;
const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);

function hexToHsl(hex: number): [number, number, number] {
  const r = ((hex >> 16) & 255) / 255;
  const g = ((hex >> 8) & 255) / 255;
  const b = (hex & 255) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) * 0.5;
  const d = max - min;
  if (d < 1e-6) return [0, 0, l];
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === r) h = (g - b) / d + (g < b ? 6 : 0);
  else if (max === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;
  return [h / 6, s, l];
}

function hueChannel(p: number, q: number, t: number): number {
  let x = t;
  if (x < 0) x += 1;
  else if (x > 1) x -= 1;
  if (x < 1 / 6) return p + (q - p) * 6 * x;
  if (x < 0.5) return q;
  if (x < 2 / 3) return p + (q - p) * (2 / 3 - x) * 6;
  return p;
}

function hslToHex(h: number, s: number, l: number): number {
  const to8 = (v: number): number => clamp(Math.round(v * 255), 0, 255);
  if (s < 1e-6) {
    const v = to8(l);
    return (v << 16) | (v << 8) | v;
  }
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  return (to8(hueChannel(p, q, h + 1 / 3)) << 16) | (to8(hueChannel(p, q, h)) << 8) | to8(hueChannel(p, q, h - 1 / 3));
}

/**
 * Per-seed colourway drift. Deliberately narrow: enough that two tiger sharks are visibly
 * different animals, never enough to cross a species into another's hue family — or into
 * `alarm`, which SPEC §15 reserves for danger and nothing else.
 */
function varyHex(rng: Rng, hex: number, dh: number, ds: number, dl: number): number {
  const [h, s, l] = hexToHsl(hex);
  let hue = h + rng.range(-dh, dh);
  if (hue < 0) hue += 1;
  else if (hue >= 1) hue -= 1;
  return hslToHex(hue, clamp(s + rng.range(-ds, ds), 0, 0.72), clamp(l + rng.range(-dl, dl), 0.04, 0.96));
}

function fin(rng: Rng, f: FinShape, length: number, girth: number): FinParams {
  return {
    height: r2(girth * f.height * (1 + rng.range(-0.08, 0.08))),
    rake: r3(clamp(f.rake + rng.range(-0.05, 0.05), 0, 1)),
    chord: r2(length * f.chord * (1 + rng.range(-0.08, 0.08))),
    position: r3(clamp(f.position + rng.range(-0.02, 0.02), 0.04, 0.96)),
  };
}

/** Both rolls always happen so the rng stream stays aligned regardless of the outcome. */
function rollGills(rng: Rng, bias: number): number {
  const sixth = rng.bool(bias);
  const seventh = rng.bool(bias * 0.35);
  return 5 + (sixth ? 1 : 0) + (sixth && seventh ? 1 : 0);
}

/**
 * Seeded from (run seed, species id) rather than the run seed alone, so the tiger and the
 * mako on one title screen never share a roll — nothing correlates across the carousel.
 */
function buildGenome(id: string, s: SpeciesShape, seed: number): SharkGenome {
  const rng = new Rng(hashInts(seed, hashString(id)));

  // Shapes below are authored at reference resolution; VOXEL.detailScale is the one lever
  // that trades grid density for triangles. Everything else in this function is derived from
  // `length` and `girth`, so scaling those two rescales the whole animal coherently.
  const k = VOXEL.detailScale;
  const length = Math.round(s.length * k * (1 + rng.range(-0.1, 0.1)));
  const girth = r2(s.girth * k * (1 + rng.range(-0.1, 0.1)));

  return normalizeGenome({
    seed,
    length,
    girth,
    // Only the two interior control points move: the endpoints are the nose and tail
    // radii, and drifting those changes which animal it is.
    taperCurve: [
      s.taper[0],
      r3(s.taper[1] * (1 + rng.range(-0.03, 0.03))),
      r3(s.taper[2] * (1 + rng.range(-0.03, 0.03))),
      s.taper[3],
    ],
    snout: s.snout,
    snoutLength: Math.max(1, Math.round(length * s.snoutLength)),
    dorsalFin: fin(rng, s.dorsal, length, girth),
    pectoralFins: fin(rng, s.pectoral, length, girth),
    caudalFin: {
      upperLobe: r2(length * s.caudal.upper),
      lowerLobe: r2(length * s.caudal.lower),
      rake: s.caudal.rake,
    },
    gillSlits: rollGills(rng, s.gillBias),
    eyeStyle: s.eyeStyle,
    pattern: s.pattern,
    palette: [
      varyHex(rng, s.palette[0], 0.02, 0.055, 0.05),
      varyHex(rng, s.palette[1], 0.018, 0.05, 0.055),
      varyHex(rng, s.palette[2], 0.01, 0.03, 0.04),
    ],
    jawWidth: r2(girth * s.jawWidth),
    toothRows: s.toothRows,
    heightRatio: s.heightRatio,
    girthPeak: s.girthPeak,
    peduncle: s.peduncle,
    secondDorsal: r2(girth * s.secondDorsal * (1 + rng.range(-0.15, 0.15))),
    pelvicFin: r2(girth * s.pelvicFin * (1 + rng.range(-0.15, 0.15))),
    headDrop: r2(rng.range(s.headDrop[0], s.headDrop[1]) * k),
    foilSpan: r2(girth * 2.4 * s.foil),
    // patternScale is noise-units per voxel; dividing by k holds the blob size constant in
    // world space so a coarser grid does not turn a mottle into a checkerboard.
    patternScale: r3(rng.range(s.patternScale[0], s.patternScale[1]) / k),
    scale: s.scale,
  });
}

// ---------------------------------------------------------------------------
// The roster
// ---------------------------------------------------------------------------
// Everything below is tuned for the read at 30 u through fog on a 62-degree camera, where
// only five things survive: snout, dorsal, pectoral sweep, caudal shape, pattern. Those
// five are pushed to the edge of plausibility on purpose; the rest is anatomy.

interface SpeciesSpec {
  id: string;
  name: string;
  hull: number;
  speed: number;
  passiveName: string;
  passive: string;
  branch: string;
  unlock: 'start' | 'denticles' | 'final';
  shape: SpeciesShape;
}

const SPECS: SpeciesSpec[] = [
  {
    id: 'tiger',
    name: 'TIGER SHARK',
    hull: 110,
    speed: 10,
    passiveName: 'Iron Stomach',
    passive: 'Eats any debris including enemy weapons; 8% chance to gain that weapon for 20 s',
    branch: 'Scavenger',
    unlock: 'start',
    shape: {
      // Long, broad-shouldered, tapering hard to a whip tail — the stripes do the rest.
      length: 44,
      girth: 8.0,
      taper: [0.34, 1.42, 0.86, 0.1],
      snout: 'shovel',
      snoutLength: 0.095, // short and wide; the blunt shovel head is the tell
      dorsal: { height: 0.85, chord: 0.17, rake: 0.44, position: 0.33 },
      pectoral: { height: 0.78, chord: 0.11, rake: 0.5, position: 0.24 },
      caudal: { upper: 0.22, lower: 0.1, rake: 0.55 }, // long trailing upper lobe
      gillBias: 0.15,
      eyeStyle: 'lateral',
      pattern: 'stripes',
      patternScale: [0.06, 0.085], // few, fat bands — cheap to mesh and legible at distance
      headDrop: [0.4, 0.9],
      palette: [0x2f4a44, 0x6e7f76, 0xe4decb], // dark teal-slate over grey-green, bone belly
      jawWidth: 0.75,
      toothRows: 2,
      heightRatio: 1.18,
      girthPeak: 0.33,
      peduncle: 0.2,
      secondDorsal: 0.28,
      pelvicFin: 0.38,
      foil: 0,
      scale: 1,
    },
  },
  {
    id: 'great-white',
    name: 'GREAT WHITE',
    hull: 130,
    speed: 10.5,
    passiveName: 'Breach',
    passive: 'Lunge from off-screen edge deals 3x damage and knocks back in a cone',
    branch: 'Ambush',
    unlock: 'start',
    shape: {
      // Heavy conical torpedo: mass forward, thin peduncle, near-lunate tail.
      length: 45,
      girth: 9.6,
      taper: [0.38, 1.5, 0.78, 0.09],
      snout: 'blunt',
      snoutLength: 0.09,
      dorsal: { height: 1.05, chord: 0.16, rake: 0.3, position: 0.35 }, // the fin everyone draws
      pectoral: { height: 0.86, chord: 0.115, rake: 0.42, position: 0.24 },
      caudal: { upper: 0.2, lower: 0.17, rake: 0.32 }, // almost symmetric crescent
      gillBias: 0.1,
      eyeStyle: 'lateral',
      pattern: 'countershaded',
      patternScale: [0.07, 0.1], // high scale = a tight, near-straight dorsal/ventral line
      headDrop: [0.6, 1.1],
      palette: [0x3f4e58, 0x5c6c76, 0xefe9d8], // steel grey slamming into bone: the hard line
      jawWidth: 0.73,
      toothRows: 3,
      heightRatio: 1.24,
      girthPeak: 0.34,
      peduncle: 0.16,
      secondDorsal: 0.19,
      pelvicFin: 0.33,
      foil: 0,
      scale: 1,
    },
  },
  {
    id: 'hammerhead',
    name: 'HAMMERHEAD',
    hull: 95,
    speed: 11,
    passiveName: 'Cephalofoil',
    passive: '360 degree enemy detection through fog; all projectiles gain slight homing',
    branch: 'Electroreception',
    unlock: 'start',
    shape: {
      // Slim laterally-compressed body so the head bar is the only wide thing on screen.
      length: 42,
      girth: 5.8,
      taper: [0.3, 1.42, 0.9, 0.09],
      snout: 'hammer',
      snoutLength: 0.075,
      dorsal: { height: 1.55, chord: 0.095, rake: 0.34, position: 0.32 }, // absurdly tall, narrow chord
      pectoral: { height: 0.8, chord: 0.08, rake: 0.56, position: 0.25 },
      caudal: { upper: 0.21, lower: 0.08, rake: 0.58 },
      gillBias: 0.15,
      eyeStyle: 'cephalofoil',
      pattern: 'countershaded',
      patternScale: [0.08, 0.11],
      headDrop: [0.05, 0.35], // the foil must stay flat or it stops reading as a bar
      palette: [0x6a6152, 0x99917e, 0xe8e2d0], // cool bronze-grey, the one warm-leaning shark
      jawWidth: 0.69,
      toothRows: 2,
      heightRatio: 1.32,
      girthPeak: 0.36,
      peduncle: 0.17,
      secondDorsal: 0.34,
      pelvicFin: 0.41,
      // 0.70 rather than the 0.60 of CONTRACTS §16: at 0.60 the bar is only 1.4x the body
      // half-width and dies in fog. 0.70 gives a ~20-voxel span on an 11-voxel body.
      foil: 0.7,
      scale: 1,
    },
  },
  {
    id: 'mako',
    name: 'SHORTFIN MAKO',
    hull: 80,
    speed: 14,
    passiveName: 'Terminal Velocity',
    passive: 'Damage scales +1% per 1% over cruise speed, up to +60%',
    branch: 'Velocity',
    unlock: 'start',
    shape: {
      // Pure fusiform dart. Everything is small except the tail, which is a symmetric blade.
      length: 38,
      girth: 5.0,
      taper: [0.26, 1.4, 0.98, 0.08],
      snout: 'pointed',
      snoutLength: 0.16, // the longest true point in the roster
      dorsal: { height: 0.9, chord: 0.1, rake: 0.26, position: 0.37 },
      pectoral: { height: 0.72, chord: 0.07, rake: 0.36, position: 0.26 }, // small scalpels
      caudal: { upper: 0.21, lower: 0.19, rake: 0.2 }, // near-equal lobes, barely raked
      gillBias: 0.1,
      eyeStyle: 'lateral',
      pattern: 'countershaded',
      patternScale: [0.09, 0.12],
      headDrop: [0.2, 0.5],
      palette: [0x27366b, 0x5a6e9b, 0xefeada], // metallic indigo over bone
      jawWidth: 0.6,
      toothRows: 2,
      heightRatio: 1.14,
      girthPeak: 0.38,
      peduncle: 0.14, // razor-thin stalk; it is what sells the lunate tail
      secondDorsal: 0.24,
      pelvicFin: 0.4,
      foil: 0,
      scale: 1,
    },
  },
  {
    id: 'whale',
    name: 'WHALE SHARK',
    hull: 200,
    speed: 7.5,
    passiveName: 'Filter Feeder',
    passive: 'Absorbs enemy projectiles into a stored charge, released as a shotgun blast. Immune to Hypoxia.',
    branch: 'Baleen',
    unlock: 'start',
    shape: {
      // Longest and fattest. Girth peaks almost at the head, so from above it reads as a
      // slab with a terminal mouth rather than a torpedo.
      length: 49,
      girth: 10.4,
      taper: [0.62, 1.34, 0.74, 0.12],
      snout: 'shovel',
      snoutLength: 0.06, // barely a snout: the head is a flat wall
      dorsal: { height: 0.58, chord: 0.13, rake: 0.4, position: 0.42 }, // small for the body, set well back
      pectoral: { height: 0.96, chord: 0.125, rake: 0.46, position: 0.26 }, // barn doors
      caudal: { upper: 0.2, lower: 0.13, rake: 0.38 },
      gillBias: 0.05,
      eyeStyle: 'lateral',
      pattern: 'spots',
      // Biggest grid in the game: hold the pattern coarse or the greedy mesher blows the
      // 900-triangle gate on spot boundaries alone.
      patternScale: [0.05, 0.062],
      headDrop: [0.1, 0.4],
      palette: [0x1b2e42, 0x46617a, 0xdcd8c6], // deep navy spots on mid slate
      jawWidth: 0.96, // mouth spans nearly the whole head
      toothRows: 1,
      heightRatio: 1.1,
      girthPeak: 0.3,
      peduncle: 0.26, // thick keeled stalk, no waist
      secondDorsal: 0.23,
      pelvicFin: 0.33,
      foil: 0,
      scale: 1,
    },
  },
  {
    id: 'goblin',
    name: 'GOBLIN SHARK',
    hull: 85,
    speed: 9,
    passiveName: 'Protrusible Jaw',
    passive: 'Lunge becomes a 14 u grapple that yanks a target to you, pre-executed if under 25%',
    branch: 'Abyssal',
    unlock: 'start',
    shape: {
      // Flabby, pear-shaped, with a blade of a snout and a tail that trails like a rag.
      length: 40,
      girth: 5.6,
      taper: [0.28, 1.16, 1.2, 0.14], // fullness pushed aft — the mass sits behind the fins
      snout: 'goblin',
      snoutLength: 0.23, // nearly a quarter of the animal is nose
      dorsal: { height: 0.66, chord: 0.12, rake: 0.5, position: 0.42 }, // soft, low, rounded
      pectoral: { height: 0.86, chord: 0.09, rake: 0.3, position: 0.28 },
      caudal: { upper: 0.26, lower: 0.05, rake: 0.72 }, // long, low, almost no lower lobe
      gillBias: 0.45,
      eyeStyle: 'stalked',
      pattern: 'solid',
      patternScale: [0.1, 0.14],
      headDrop: [0.3, 0.8],
      palette: [0x8e7c88, 0xbaaab3, 0xede4e2], // pale mauve; cold and bloodless, never enemy-warm
      jawWidth: 0.89,
      toothRows: 3, // needle rows, the one thing that is sharp about it
      heightRatio: 1.08,
      girthPeak: 0.42,
      peduncle: 0.24,
      secondDorsal: 0.39,
      pelvicFin: 0.46,
      foil: 0,
      scale: 1,
    },
  },
  {
    id: 'thresher',
    name: 'THRESHER',
    hull: 100,
    speed: 11.5,
    passiveName: 'Tail Whip',
    passive: 'Every 4th Lunge ends in a 6 u AoE tail slam that stuns',
    branch: 'Kinetic',
    unlock: 'start',
    shape: {
      // Short body on purpose so the scythe reads as longer than the shark. The lobe is a
      // 1-2 voxel slab, so it merges into a handful of quads and costs almost nothing.
      length: 34,
      girth: 6.0,
      taper: [0.28, 1.44, 0.9, 0.08],
      snout: 'pointed',
      snoutLength: 0.115,
      dorsal: { height: 1.2, chord: 0.105, rake: 0.38, position: 0.35 },
      pectoral: { height: 1.28, chord: 0.07, rake: 0.66, position: 0.25 }, // long swept scythes, echoing the tail
      caudal: { upper: 0.82, lower: 0.09, rake: 0.5 }, // 82% of body length. The species.
      gillBias: 0.15,
      eyeStyle: 'lateral',
      pattern: 'countershaded',
      patternScale: [0.08, 0.11],
      headDrop: [0.3, 0.7],
      palette: [0x413a53, 0x6e667f, 0xe7e2d2], // violet-charcoal, iridescent-looking under the rim light
      jawWidth: 0.5,
      toothRows: 1,
      heightRatio: 1.26,
      girthPeak: 0.36,
      peduncle: 0.15,
      secondDorsal: 0.23,
      pelvicFin: 0.37,
      foil: 0,
      scale: 1,
    },
  },
  {
    id: 'greenland',
    name: 'GREENLAND SHARK',
    hull: 160,
    speed: 6.5,
    passiveName: 'Centenarian',
    passive: 'One free revive per run at 40% hull; slow but immune to Corroded',
    branch: 'Glacial',
    unlock: 'start',
    shape: {
      // A log. Near-constant radius, no waist, and fins so small they barely break the
      // outline — the exact opposite silhouette to the great white.
      length: 47,
      girth: 9.0,
      taper: [0.52, 1.2, 1.02, 0.16],
      snout: 'blunt',
      snoutLength: 0.065,
      dorsal: { height: 0.35, chord: 0.085, rake: 0.34, position: 0.5 }, // vestigial, set far back
      pectoral: { height: 0.46, chord: 0.07, rake: 0.26, position: 0.27 }, // stubs
      caudal: { upper: 0.16, lower: 0.11, rake: 0.46 }, // short and paddle-like
      gillBias: 0.35,
      eyeStyle: 'lateral',
      pattern: 'mottle',
      patternScale: [0.055, 0.075], // coarse three-band mottle; fine noise would cost triangles
      headDrop: [0.2, 0.6],
      palette: [0x3d473c, 0x626b5a, 0xc9c7b2], // drab olive-slate, the murkiest palette in the game
      jawWidth: 0.56,
      toothRows: 1,
      heightRatio: 1.09,
      girthPeak: 0.4,
      peduncle: 0.29, // thick to the very end
      secondDorsal: 0.29, // two near-identical tiny dorsals
      pelvicFin: 0.31,
      foil: 0,
      scale: 1,
    },
  },
  {
    id: 'cookiecutter',
    name: 'COOKIECUTTER',
    hull: 60,
    speed: 12,
    passiveName: 'Excise',
    passive: 'Tiny hitbox; every bite steals a random buff from the target for 10 s',
    branch: 'Parasite',
    unlock: 'denticles',
    shape: {
      // A cigar with a tail. Shortest and roundest; everything is pushed to the back third
      // so it reads as a dart at a glance.
      length: 26,
      girth: 4.6,
      taper: [0.46, 1.16, 1.1, 0.16],
      snout: 'blunt',
      snoutLength: 0.08,
      dorsal: { height: 0.5, chord: 0.11, rake: 0.3, position: 0.6 }, // way aft, near the tail
      pectoral: { height: 0.52, chord: 0.075, rake: 0.22, position: 0.3 },
      caudal: { upper: 0.2, lower: 0.17, rake: 0.26 },
      gillBias: 0.2,
      eyeStyle: 'stalked', // the only way an oversized eye survives a top-down camera
      pattern: 'countershaded',
      patternScale: [0.1, 0.14],
      headDrop: [0.0, 0.25],
      palette: [0x23282e, 0x455057, 0xa9e4d9], // near-black over a bioluminescent `caustic` belly
      jawWidth: 0.65,
      toothRows: 2,
      heightRatio: 1.06,
      girthPeak: 0.44,
      peduncle: 0.29,
      secondDorsal: 0, // suppressed: on a 26-voxel body a second fin is visual noise
      pelvicFin: 0.3,
      foil: 0,
      scale: 1,
    },
  },
  {
    id: 'megalodon',
    name: 'MEGALODON',
    hull: 240,
    speed: 9,
    passiveName: 'Extinct',
    passive: '2x size, bodies-as-weapons collision damage, but enemies spawn 25% faster',
    branch: 'Apex',
    unlock: 'final',
    shape: {
      // The great white's proportions with every mass value pushed up, then doubled in
      // world space. `solid` and a coarse pattern scale keep the largest grid inside budget.
      length: 46,
      girth: 10.4,
      taper: [0.42, 1.48, 0.8, 0.1],
      snout: 'blunt',
      snoutLength: 0.09,
      dorsal: { height: 1.02, chord: 0.165, rake: 0.28, position: 0.34 },
      pectoral: { height: 0.92, chord: 0.125, rake: 0.4, position: 0.24 },
      caudal: { upper: 0.22, lower: 0.18, rake: 0.3 },
      gillBias: 0.1,
      eyeStyle: 'lateral',
      pattern: 'solid',
      patternScale: [0.05, 0.07],
      headDrop: [0.7, 1.3], // heavy brow; the head hangs
      palette: [0x141f2a, 0x2c3b46, 0xb9b6a6], // abyssal near-black, dull bone
      jawWidth: 0.87,
      toothRows: 3,
      heightRatio: 1.28,
      girthPeak: 0.33,
      peduncle: 0.17,
      secondDorsal: 0.19,
      pelvicFin: 0.33,
      foil: 0,
      scale: 2,
    },
  },
];

/**
 * Tiger model settings. `res` is voxels along the model's long axis and cost scales roughly
 * with its cube. 200 is affordable here in a way it is not for an instanced-cube renderer:
 * the sculpt is a one-time cost in a worker, and greedy meshing collapses the shell to a
 * few thousand triangles rather than ~80k cubes.
 */
const TIGER_RES = 200;
/** finished nose-to-tail length in world units; the rest of the roster lands near 8 */
const TIGER_LENGTH = 9.43;
const TIGER_SNOUT = 0.6;
const TIGER_AGE = 0.18;

export const SPECIES: SpeciesDef[] = SPECS.map((spec) => ({
  id: spec.id,
  name: spec.name,
  hull: spec.hull,
  speed: spec.speed,
  passiveName: spec.passiveName,
  passive: spec.passive,
  branch: spec.branch,
  unlock: spec.unlock,
  genome: (seed: number): SharkGenome => {
    const g = buildGenome(spec.id, spec.shape, seed);
    // The tiger is the default playable shark and gets a hand-tuned model: parametric
    // profile tables, polygon fins, a carved oral cavity and an articulated jaw. The
    // parametric fields above are still filled in so stats, palette and the lab readouts
    // stay uniform across the roster — TigerSculpt just ignores the shape ones.
    if (spec.id === 'tiger') {
      g.model = 'tiger';
      g.tigerRes = TIGER_RES;
      g.tigerSnout = TIGER_SNOUT;
      g.tigerAge = TIGER_AGE;
      g.tigerLength = TIGER_LENGTH;
    }
    return g;
  },
}));

export const SPECIES_BY_ID: Record<string, SpeciesDef> = Object.fromEntries(
  SPECIES.map((s): [string, SpeciesDef] => [s.id, s]),
);

/** Unknown ids fall back to the starting species rather than throwing inside a render loop. */
export function speciesGenome(id: string, seed: number): SharkGenome {
  const def = SPECIES_BY_ID[id] ?? SPECIES[0];
  return def.genome(seed);
}
